// src/components/GoogleSetupModal.jsx
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import authService from '../services/authService';
import './GoogleSetupModal.css';

const USERNAME_RULES = /^[a-zA-Z0-9_]+$/;

/* ─── Inline SVGs ───────────────────────────────────────── */
const IconUser = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" className="gsm-field__icon">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const IconLock = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" className="gsm-field__icon">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);

const IconEyeOpen = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const IconEyeOff = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const IconCheck = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"
    strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const IconX = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"
    strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

/* ─── Username availability badge ───────────────────────── */
function UsernameBadge({ status, username }) {
  if (!username || username.length < 3) return null;
  if (status === 'checking') return (
    <span className="gsm-badge gsm-badge--checking">
      <span className="gsm-badge__spinner" /> Checking…
    </span>
  );
  if (status === 'available') return (
    <span className="gsm-badge gsm-badge--ok"><IconCheck /> Available</span>
  );
  if (status === 'taken' || status === 'invalid') return (
    <span className="gsm-badge gsm-badge--err">
      <IconX />
      {status === 'taken' ? 'Already taken' : 'Letters, numbers & _ only'}
    </span>
  );
  return null;
}

/* ─── Password strength bar ─────────────────────────────── */
function StrengthBar({ password }) {
  if (!password) return null;
  let score = 0;
  if (password.length >= 8)              score++;
  if (password.length >= 12)             score++;
  if (/[A-Z]/.test(password))           score++;
  if (/[0-9]/.test(password))           score++;
  if (/[^a-zA-Z0-9]/.test(password))   score++;

  const label  = ['', 'Weak', 'Fair', 'Good', 'Strong', 'Very strong'][score];
  const cls    = ['', 'weak', 'fair', 'good', 'strong', 'very-strong'][score];

  return (
    <div className="gsm-strength">
      <div className="gsm-strength__bars">
        {[1,2,3,4,5].map((n) => (
          <span key={n} className={`gsm-strength__bar${n <= score ? ` gsm-strength__bar--${cls}` : ''}`} />
        ))}
      </div>
      <span className={`gsm-strength__label gsm-strength__label--${cls}`}>{label}</span>
    </div>
  );
}

/* ─── Main Modal ─────────────────────────────────────────── */
export default function GoogleSetupModal() {
  const { user, completeGoogleSetup, logout } = useAuth();
  const navigate = useNavigate();

  const [username,        setUsername]        = useState('');
  const [usernameStatus,  setUsernameStatus]  = useState(null);
  const [password,        setPassword]        = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPass,        setShowPass]        = useState(false);
  const [showConf,        setShowConf]        = useState(false);
  const [errors,          setErrors]          = useState({});
  const [apiError,        setApiError]        = useState('');
  const [loading,         setLoading]         = useState(false);
  const [done,            setDone]            = useState(false);

  const debounceRef = useRef(null);

  /* ── Handle back button: logout + redirect to /login with message ── */
  useEffect(() => {
    // Push a dummy history entry so we can intercept the back button
    window.history.pushState(null, '', window.location.href);

    const handlePopState = () => {
      logout();
      navigate('/login', {
        replace: true,
        state: { loginError: 'Login failed. Please sign in again to continue.' },
      });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [logout, navigate]);

  const suggestedUsername = (user?.fullName || '')
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 20);

  /* ── Username debounce check ── */
  const checkUsername = useCallback((val) => {
    if (!val || val.length < 3) { setUsernameStatus(null); return; }
    if (!USERNAME_RULES.test(val)) { setUsernameStatus('invalid'); return; }
    setUsernameStatus('checking');
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await authService.checkUsername(val);
        setUsernameStatus(res.available ? 'available' : 'taken');
      } catch {
        setUsernameStatus(null);
      }
    }, 600);
  }, []);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const handleUsernameChange = (e) => {
    const val = e.target.value;
    setUsername(val);
    if (errors.username) setErrors((p) => ({ ...p, username: '' }));
    setApiError('');
    checkUsername(val);
  };

  const validate = () => {
    const errs = {};
    if (!username.trim()) {
      errs.username = 'Username is required';
    } else if (username.length < 3) {
      errs.username = 'Minimum 3 characters';
    } else if (!USERNAME_RULES.test(username)) {
      errs.username = 'Only letters, numbers and underscores';
    } else if (usernameStatus === 'taken') {
      errs.username = 'Username already taken';
    } else if (usernameStatus === 'checking') {
      errs.username = 'Please wait for availability check…';
    } else if (usernameStatus === 'invalid') {
      errs.username = 'Only letters, numbers and underscores';
    }

    if (!password) {
      errs.password = 'Password is required';
    } else if (password.length < 8) {
      errs.password = 'Minimum 8 characters';
    }

    if (!confirmPassword) {
      errs.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      errs.confirmPassword = 'Passwords do not match';
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setApiError('');

    try {
      const result = await completeGoogleSetup({ username: username.trim(), password });
      if (result.success) {
        setDone(true);
      } else {
        setApiError(result.message || 'Setup failed. Please try again.');
      }
    } catch {
      setApiError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /* ── Handle "Sign out" escape hatch: also show login failed message ── */
  const handleSignOut = () => {
    logout();
    navigate('/login', {
      replace: true,
      state: { loginError: 'Login failed. Please sign in again to continue.' },
    });
  };

  /* ── Success screen ── */
  if (done) {
    return (
      <div className="gsm-overlay">
        <div className="gsm-card gsm-card--success">
          <div className="gsm-success-ring">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
              width="36" height="36">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h2 className="gsm-success-title">You're all set!</h2>
          <p className="gsm-success-sub">
            Welcome to Tripsie, <strong>{user?.fullName?.split(' ')[0]}</strong>. Your profile is ready.
          </p>
        </div>
      </div>
    );
  }

  /* ── Setup form ── */
  return (
    <div className="gsm-overlay" role="dialog" aria-modal="true" aria-labelledby="gsm-title">
      <div className="gsm-card">

        {/* Header */}
        <div className="gsm-header">
          <div className="gsm-avatar-wrap">
            {user?.avatar
              ? <img src={user.avatar} alt={user.fullName} className="gsm-avatar" />
              : <div className="gsm-avatar gsm-avatar--placeholder">
                  {(user?.fullName || 'U')[0].toUpperCase()}
                </div>
            }
            <div className="gsm-google-badge" title="Google account">
              <svg viewBox="0 0 24 24" width="12" height="12">
                <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            </div>
          </div>

          <div className="gsm-header-text">
            <h2 id="gsm-title" className="gsm-title">One last step</h2>
            <p className="gsm-subtitle">
              Hi <strong>{user?.fullName?.split(' ')[0] || 'there'}</strong>! Choose a username and
              password so you can also sign in with email.
            </p>
          </div>
        </div>

        {/* Email (read-only context) */}
        <div className="gsm-email-badge">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
            <polyline points="22,6 12,13 2,6"/>
          </svg>
          {user?.email}
        </div>

        {/* Error */}
        {apiError && (
          <div className="gsm-alert">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            {apiError}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate className="gsm-form">

          {/* Username */}
          <div className={`gsm-field${errors.username ? ' gsm-field--error' : ''}`}>
            <label className="gsm-field__label" htmlFor="gsm-username">Username</label>
            <div className="gsm-field__wrap">
              <IconUser />
              <input
                id="gsm-username"
                type="text"
                className="gsm-field__input"
                placeholder={suggestedUsername || 'e.g. adventure_raj'}
                value={username}
                onChange={handleUsernameChange}
                autoComplete="username"
                spellCheck={false}
                autoCapitalize="none"
                autoFocus
                maxLength={30}
              />
            </div>
            {errors.username
              ? <span className="gsm-field__err">{errors.username}</span>
              : <UsernameBadge status={usernameStatus} username={username} />
            }
          </div>

          {/* Password */}
          <div className={`gsm-field${errors.password ? ' gsm-field--error' : ''}`}>
            <label className="gsm-field__label" htmlFor="gsm-password">Password</label>
            <div className="gsm-field__wrap">
              <IconLock />
              <input
                id="gsm-password"
                type={showPass ? 'text' : 'password'}
                className="gsm-field__input"
                placeholder="Min 8 characters"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password) setErrors((p) => ({ ...p, password: '' }));
                  setApiError('');
                }}
                autoComplete="new-password"
              />
              <button type="button" className="gsm-field__eye"
                onClick={() => setShowPass((p) => !p)}
                aria-label={showPass ? 'Hide password' : 'Show password'}>
                {showPass ? <IconEyeOff /> : <IconEyeOpen />}
              </button>
            </div>
            {errors.password
              ? <span className="gsm-field__err">{errors.password}</span>
              : <StrengthBar password={password} />
            }
          </div>

          {/* Confirm Password */}
          <div className={`gsm-field${errors.confirmPassword ? ' gsm-field--error' : ''}`}>
            <label className="gsm-field__label" htmlFor="gsm-confirm">Confirm Password</label>
            <div className="gsm-field__wrap">
              <IconLock />
              <input
                id="gsm-confirm"
                type={showConf ? 'text' : 'password'}
                className="gsm-field__input"
                placeholder="Repeat your password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (errors.confirmPassword) setErrors((p) => ({ ...p, confirmPassword: '' }));
                  setApiError('');
                }}
                autoComplete="new-password"
              />
              <button type="button" className="gsm-field__eye"
                onClick={() => setShowConf((p) => !p)}
                aria-label={showConf ? 'Hide password' : 'Show password'}>
                {showConf ? <IconEyeOff /> : <IconEyeOpen />}
              </button>
            </div>
            {errors.confirmPassword && (
              <span className="gsm-field__err">{errors.confirmPassword}</span>
            )}
          </div>

          {/* Submit */}
          <button type="submit" className="gsm-btn" disabled={loading}>
            {loading
              ? <><span className="gsm-btn__spinner" /> Saving…</>
              : 'Complete Setup'
            }
          </button>
        </form>

        {/* Escape hatch */}
        <div className="gsm-footer">
          <p>Changed your mind?{' '}
            <button type="button" className="gsm-logout-link" onClick={handleSignOut}>
              Sign out
            </button>
          </p>
        </div>

      </div>
    </div>
  );
}